import { useCallback, useMemo } from 'react';
import { ArtistService } from '../service/artist/ArtistService';
import { getArtistService } from '../service/ServiceRegistry';
import type {
  Artist,
  ArtistsQueryParams,
  CreateArtistRequest,
  UpdateArtistRequest,
} from '../types/api/artist';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';

export function useArtists() {
  const service = useMemo(() => new ArtistService(), []);

  const fetchArtists: FetchDataFn<Artist> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Artist>> => {
      const query: ArtistsQueryParams = {
        page: params.page,
        size: params.pageSize,
        keyword: params.search,
      };
      const response = await service.getArtists(query);
      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [service]
  );

  return { fetchArtists };
}

export function useArtistActions() {
  const service = getArtistService();

  const createArtist = useCallback(
    async (data: CreateArtistRequest, imageFile?: File): Promise<Artist> => {
      return service.createArtist(data, imageFile);
    },
    [service]
  );

  const updateArtist = useCallback(
    async (artistId: string, data: UpdateArtistRequest, imageFile?: File): Promise<Artist> => {
      return service.updateArtist(artistId, data, imageFile);
    },
    [service]
  );

  const getArtist = useCallback(
    async (artistId: string): Promise<Artist> => {
      return service.getArtist(artistId);
    },
    [service]
  );

  const deleteArtist = useCallback(
    async (artistId: string): Promise<void> => {
      return service.deleteArtist(artistId);
    },
    [service]
  );

  return { createArtist, updateArtist, getArtist, deleteArtist };
}
